import { getRuntimeConfig } from '@iheartjane/runtime-config';
import type { AppMode } from '@iheartjane/types';
import { Storage, statePostalCode } from '@iheartjane/utils';
import { differenceInYears, parseISO } from 'date-fns';
import { isbot } from 'isbot';
import isEmpty from 'lodash-es/isEmpty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useParams } from 'react-router';

import { UserLocation } from '@jane/monolith/components/userLocation';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { AgeGateModal } from '@jane/shared-ecomm/components';
import { useQuery } from '@jane/shared-ecomm/hooks';
import { useUserPreferences } from '@jane/shared-ecomm/providers';
import { useJaneUser } from '@jane/shared/data-access';
import { useSafeState } from '@jane/shared/hooks';
import { useEcommApp } from '@jane/shared/providers';

const CONFIRM_AGE_GATE_EXPIRE_TIME_STORAGE_KEY = 'confirmAgeGateExpireTime';
const CONFIRM_MED_PATIENT_EXPIRE_TIME_STORAGE_KEY =
  'confirmMedPatientExpireTime';
const CONFIRM_BIRTH_DATE_EXPIRE_TIME_STORAGE_KEY = 'confirmBirthDateExpireTime';

const getInitialConditions = (
  appMode: AppMode,
  storageKey: string,
  userBirthDate?: string | null
) => {
  if (
    userBirthDate &&
    differenceInYears(new Date(), parseISO(userBirthDate)) >= 21
  ) {
    return true;
  }

  const confirmConditionsExpireTime = Storage.get<number>(storageKey);
  const confirmConditionsExpired = confirmConditionsExpireTime
    ? confirmConditionsExpireTime <= new Date().getTime()
    : true;

  if (confirmConditionsExpired) {
    Storage.remove(storageKey);
  }

  if (appMode === 'default') {
    return !confirmConditionsExpired;
  }

  return (
    !confirmConditionsExpired || // user has clicked accept already with remember me for 30 days
    document.cookie.includes('age_gate') || // wordpress age gate plugin sets this cookie
    isbot(navigator.userAgent) || // is it a search crawler?
    getRuntimeConfig().showAgeGate === false || // turned off in business app
    false // we should show the modal
  );
};

// 30 days
const REMEMBER_ME_MILLISECONDS = 30 * 24 * 60 * 60 * 1000;

// Dictionary of states that require birth date verification
const STATES_NEED_BIRTH_DATE: Record<string, string> = { ['MD']: 'Maryland' };

export const ApplicationGates = ({ children }: { children?: ReactElement }) => {
  const { userLocation } = useUserPreferences();
  const { store } = useCustomerSelector(selectStore);
  const { appMode } = useEcommApp();
  const query = useQuery<{ skipgate?: string }>();
  const neverShowGatesRef = useRef(query.skipgate === '1');
  const { data: janeUser } = useJaneUser();
  const userBirthDate = janeUser?.user?.birth_date;

  // no location modal on dispensary page when location is defined
  const { country } = useParams<{ country: string }>();

  const [rememberMe, setRememberMe] = useState(false);
  const [confirmedAgeGateState, setConfirmedAgeGateState] = useSafeState(
    getInitialConditions(
      appMode,
      CONFIRM_AGE_GATE_EXPIRE_TIME_STORAGE_KEY,
      userBirthDate
    )
  );
  const [confirmedMedPatientState, setConfirmedMedPatientState] = useSafeState(
    getInitialConditions(appMode, CONFIRM_MED_PATIENT_EXPIRE_TIME_STORAGE_KEY)
  );
  const [confirmedBirthDateState, setConfirmedBirthDateState] = useSafeState(
    getInitialConditions(
      appMode,
      CONFIRM_BIRTH_DATE_EXPIRE_TIME_STORAGE_KEY,
      userBirthDate
    )
  );

  const userStateMatch = userLocation?.cityState?.match(/,\s(\D{2})/);
  const userStateAbbreviated = store
    ? statePostalCode(store.state)
    : !isEmpty(userStateMatch)
      ? userStateMatch![1]
      : null;
  const stateRequiresBirthDate: string | undefined = userStateAbbreviated
    ? STATES_NEED_BIRTH_DATE[userStateAbbreviated]
    : undefined;

  const showAgeGateModal =
    !neverShowGatesRef.current &&
    !(confirmedAgeGateState || confirmedMedPatientState);
  const showBirthDateModal: boolean =
    !!stateRequiresBirthDate &&
    !neverShowGatesRef.current &&
    !confirmedBirthDateState &&
    !confirmedMedPatientState;

  const onConfirm = useCallback(
    (type: 'age-gate' | 'med-patient' | 'birth-date') => {
      const now = new Date();
      const expire = now.getTime() + REMEMBER_ME_MILLISECONDS;

      if (type === 'age-gate') {
        rememberMe &&
          Storage.set(CONFIRM_AGE_GATE_EXPIRE_TIME_STORAGE_KEY, expire);
        setConfirmedAgeGateState(true);
      } else if (type === 'birth-date') {
        rememberMe &&
          Storage.set(CONFIRM_AGE_GATE_EXPIRE_TIME_STORAGE_KEY, expire);
        rememberMe &&
          Storage.set(CONFIRM_BIRTH_DATE_EXPIRE_TIME_STORAGE_KEY, expire);
        setConfirmedBirthDateState(true);
        setConfirmedAgeGateState(true);
      } else {
        rememberMe &&
          Storage.set(CONFIRM_MED_PATIENT_EXPIRE_TIME_STORAGE_KEY, expire);
        setConfirmedMedPatientState(true);
      }
    },
    [
      rememberMe,
      setConfirmedAgeGateState,
      setConfirmedBirthDateState,
      setConfirmedMedPatientState,
    ]
  );

  const ageGateModalProps = useMemo(
    () => ({
      rememberMe,
      setRememberMe,
      showAgeGateModal,
      showBirthDateModal,
      onConfirm,
      state: stateRequiresBirthDate,
    }),
    [
      rememberMe,
      setRememberMe,
      showAgeGateModal,
      showBirthDateModal,
      onConfirm,
      stateRequiresBirthDate,
    ]
  );

  //If a user's birthdate changes (i.e. they sign in or out), we need to re-check their age-based conditions
  useEffect(() => {
    setConfirmedAgeGateState(
      getInitialConditions(
        appMode,
        CONFIRM_AGE_GATE_EXPIRE_TIME_STORAGE_KEY,
        userBirthDate
      )
    );
    setConfirmedBirthDateState(
      getInitialConditions(
        appMode,
        CONFIRM_BIRTH_DATE_EXPIRE_TIME_STORAGE_KEY,
        userBirthDate
      )
    );
  }, [userBirthDate]);

  if (neverShowGatesRef.current) {
    return children;
  }

  if (showAgeGateModal || showBirthDateModal) {
    return (
      <>
        {children}
        <AgeGateModal {...ageGateModalProps} />
      </>
    );
  }

  if (appMode === 'default' && !country) {
    return <UserLocation>{children}</UserLocation>;
  }

  return children;
};
